<template>
    <div class="col_item" :class="{ bold: isBold, block: type == 'block', work_block: type == 'workCode' }"
        :style="styleString" :title="titleOverlay" @click.right="action_RightClick" @dblclick="action_DoubleClick"
        @mousedown.left.prevent="action_MouseDown" @mousemove.left.prevent="action_MouseMove"
        @mouseup.left.prevent="action_MouseUp">
        <span class="ellipsis" v-if="isFirst" :class="{ white_txt: type == 'color' && !this.isLightBackground }">
            <v-icon small v-if="type == 'workCode'">mdi-calendar-account</v-icon>{{ title }}
        </span>
        <div class="col_color" v-if="type != 'none' && isSelected" />
    </div>
</template>

<script>
import { deepCopy } from '@/util/deep-copy'
import { typeToString } from '@/util/type-to-string'
import { sheetState } from '../../../sheet-state'
import { closeHServiceSearch, hideCellMenu, hideColumnMenu, hideGroupMenu, hideReservationMenu, sheetOverlays, showCellMenu, showHServiceSearch, showReservationInfoPopup } from '../../../sheet-overlays'
import { TimeSelectModeType, exitTimeSelectMode, reservationSelection, setSelectedReservation } from '../../../reservation-selection'
import { sheetAxis } from '../../../sheet-axis'
import { sheetFilters, selectedColumns, selectedGroups } from '../../../sheet-filters'
import { createReservation, updateReservation } from '../../../reservation-actions'
import moment from '@/util/MPMoment'
import { mapGetters } from 'vuex'

export default {
    props: {
        xPosition: Number,
        column: Object,
        timeData: Object,
        blockOrColor: Object,
        workCode: Object,
        selectedAreaHelper: Object,
        boldLineTimes: Array,
        originalBlocksAndColors: Array
    },
    data() {
        return {
            sheetState,
            sheetOverlays,
            reservationSelection,
            sheetAxis,
            sheetFilters,
        }
    },
    computed: {
        ...mapGetters(['CellConfig_FindColumnWidth']),
        time: function () {
            return this.timeData.time
        },
        timeString: function () {
            return this.timeData.timeString
        },
        timeArray: function () {
            return this.sheetAxis.timeArray
        },
        timeInterval: function () {
            return this.sheetAxis.selectedTimeInterval
        },
        isBold: function () {
            return this.boldLineTimes?.some(t => t == this.timeString) ?? false
        },
        yPosition: function () {
            return this.timeData.index
        },
        columnID: function () {
            return this.column?._id
        },
        groupID: function () {
            return this.column?.groupID
        },
        type: function () {
            if (this.workCode != undefined) {
                return 'workCode'
            } else if (this.blockOrColor != undefined) {
                return this.blockOrColor.cellType
            } else {
                return 'none'
            }
        },
        title: function () {
            switch (this.type) {
                case 'block':
                case 'color':
                    return this.blockOrColor.note
                case 'workCode': {
                    let code = this.workCode.isCustomized ? '직접설정' : this.workCode.title
                    return `${this.workCode.employeeTitle}[${code || ''}]`
                }
                default:
                    return ''
            }
        },
        titleOverlay: function () {
            let title = this.title
            if (this.type == 'workCode') {
                if (this.workCode.isDefault) {
                    title += '\n하루종일'
                } else {
                    let s = moment(this.workCode.startedAt).format('HH:mm')
                    let e = moment(this.workCode.endedAt).format('HH:mm')
                    title += `\n${s} ~ ${e}`
                }
            }
            return title
        },
        isFirst: function () {
            let cellData = this.type == 'workCode' ? this.workCode : this.blockOrColor
            return cellData?.isFirstIndex == true
        },
        range: function () {
            return this.selectedAreaHelper.selectedRange
        },
        startRange: function () {
            let x = Math.min(this.range.start.x, this.range.end.x)
            let y = Math.min(this.range.start.y, this.range.end.y)
            return { x, y }
        },
        endRange: function () {
            let x = Math.max(this.range.start.x, this.range.end.x)
            let y = Math.max(this.range.start.y, this.range.end.y)
            return { x, y }
        },
        selectedTimeRange: function () {
            let start = moment(this.timeArray[this.startRange.y].time, 'HH:mm')
            let end = moment(this.timeArray[this.endRange.y].time, 'HH:mm').add(this.timeInterval - 1, 'minutes')
            return { start, end }
        },
        isSelected: function () {
            let infX = this.xPosition >= this.startRange.x && this.xPosition <= this.endRange.x
            let infY = this.yPosition >= this.startRange.y && this.yPosition <= this.endRange.y
            return infX && infY
        },
        filteredColumns: function () {
            let groups = deepCopy(selectedGroups())

            return groups
                .sort((l, r) => l?.order ?? 0 - (r?.order ?? 0))
                .reduce((acc, group) => {
                    let ids = selectedColumns().filter((c) => c.isSelected && c.groupID == group._id)

                    acc.push(ids.length > 0 ? ids : { _id: undefined })
                    return acc
                }, [])
                .flat()
        },
        selectedRangeCellData: function () {
            let start = this.startRange.x
            let end = this.endRange.x
            let selectedColumns = this.filteredColumns.slice(start, end + 1)
            return this.originalBlocksAndColors.filter((cell) => {
                if (!selectedColumns.some((item) => cell.columns.includes(item._id))) {
                    return false
                }
                let start = moment(moment(cell.startedAt).toString())
                let end = moment(moment(cell.endedAt).toString())
                return this.selectedTimeRange.start.isBefore(end) && this.selectedTimeRange.end.isAfter(start)
            })
        },
        emptyReservationObject: function () {
            let timeStrings = this.timeData.timeString.split(':')
            let hour = timeStrings[0]
            let minute = timeStrings[1]
            let startedAt = moment(this.sheetAxis.selectedDate).startOf('days').set('hours', hour).set('minutes', minute)
            return {
                groupID: this.groupID,
                columnID: this.columnID,
                startedAt: startedAt.toString()
            }
        },
        height: function () {
            return this.timeData?.height ?? 26
        },
        styleString: function () {
            return `background: ${this.backgroundColor}; flex-basis: unset; height: ${this.height}px;`
        },
        backgroundColor: function () {
            if (this.type == 'color') {
                return this.blockOrColor.usingCustomColor ? this.blockOrColor.customColorCode : this.palette[this.blockOrColor.paletteIndex]
            }
            return ''
        },
        palette: function () {
            return this.sheetState.reservationSetting?.cellColorPaletteID?.basicPalette
        },
        brightness: function () {
            let color = this.backgroundColor?.slice(1) ?? '#FF0000'
            let rgb = parseInt(color, 16)
            let r = (rgb >> 16) & 0xff
            let g = (rgb >> 8) & 0xff
            let b = (rgb >> 0) & 0xff
            return 0.299 * r + 0.587 * g + 0.114 * b
        },
        isLightBackground: function () {
            if (this.type == "color") {
                return this.brightness > 111
            } else {
                return true
            }
        },
        hservice: function () {
            return this.getCopiedReservation().hserviceID
        },
        isJoin: function () {
            return this.hservice?.lastLogin != undefined
        },
        isClosedService: function () {
            const closeReason = this.hservice.closeReason ?? []
            return closeReason.length > 0
        },
        isUsingKakaoAlrimTalk: function () {
            return this.servicesetting.setting.usingKakaoAlimAndFriendTalkLinkage
        },
        canSendMessage: function () {
            if (this.hservice.linkType === '10000') { return false }
            return (this.isClosedService == false) && (this.isUsingKakaoAlrimTalk == true || this.isJoin == true)
        }
    },
    methods: {
        // MARK: - Action
        action_RightClick(e) {
            if (this.sheetOverlays.isShowHServiceSearch) {
                closeHServiceSearch()
            }
            if (this.sheetOverlays.isShowReservationMenu) {
                hideReservationMenu()
            }
            if (this.sheetOverlays.isShowGroupMenu) {
                hideGroupMenu()
            }
            if (this.sheetOverlays.isShowColumnMenu) {
                hideColumnMenu()
            }
            if (this.reservationSelection.isTimeSelectMode) {
                return
            }
            if (!this.isSelected) {
                this.selectCurrentPosition(e)
                return
            }
            let position = this.getMousePosition(e)
            this.sheetOverlays.isShowCellMenu = false
            this.$nextTick(() => {
                showCellMenu(position, this.selectedRangeCellData)
            })
        },
        action_DoubleClick() {
            if (this.reservationSelection.isTimeSelectMode) {
                switch (this.reservationSelection.timeSelectModeType) {
                    case TimeSelectModeType.none:
                    case TimeSelectModeType.fromSaaS:
                        this.openReservationEditPopup()
                        break
                    case TimeSelectModeType.copy:
                        this.copySelectedReservation()
                        break
                    case TimeSelectModeType.cut:
                        this.cutPasteSelectedReservation()
                        break
                    default:
                        break
                }
            } else {
                if (this.columnID == undefined) { return }
                this.openSearchServicePopup()
            }
        },
        action_MouseDown() {
            if (!this.reservationSelection.isTimeSelectMode) {
                setSelectedReservation(undefined)
            }
            closeHServiceSearch()
            hideCellMenu()
            this.selectedAreaHelper.setType(this.selectedAreaHelper.Type.cell)
            this.selectedAreaHelper.setStartPoint(this.xPosition, this.yPosition)
        },
        action_MouseMove() {
            if (this.selectedAreaHelper == undefined) { return }
            if (this.selectedAreaHelper.startPoint.x == undefined) { return }
            if (this.selectedAreaHelper.currentType != this.selectedAreaHelper.Type.cell) { return }

            this.selectedAreaHelper.setEndPoint(this.xPosition, this.yPosition)
            let start = this.selectedAreaHelper.startPoint
            let end = this.selectedAreaHelper.endPoint
            this.selectedAreaHelper.setSelectedRange(start.x, start.y, end.x, end.y)
        }
        ,
        action_MouseUp() {
            if (this.selectedAreaHelper.currentType != this.selectedAreaHelper.Type.cell) { return }
            this.selectedAreaHelper.setEndPoint(this.xPosition, this.yPosition)
            let start = this.selectedAreaHelper.startPoint
            let end = this.selectedAreaHelper.endPoint
            this.selectedAreaHelper.setSelectedRange(start.x, start.y, end.x, end.y)
            this.selectedAreaHelper.clearStartEndPoint()
        },
        // MARK: - Private
        getMousePosition(e) {
            let sheetRect = this.sheetState.sheetElement.getBoundingClientRect()
            let scrollRect = this.sheetState.sheetScrollElement
            let x = e.clientX - sheetRect.x + scrollRect.scrollLeft
            let y = e.clientY - sheetRect.y + scrollRect.scrollTop
            return { x: x, y: y }
        },
        getCurrentComponentPosition() {
            let target = this.$el.getBoundingClientRect()
            let sheetRect = this.sheetState.sheetElement.getBoundingClientRect()
            let scrollRect = this.sheetState.sheetScrollElement
            let x = target.left - sheetRect.x + scrollRect.scrollLeft
            let y = (this.timeData.topMargin + this.timeData?.height) ?? 0
            return { x, y }
        },
        openSearchServicePopup() {
            let target = this.getCurrentComponentPosition()
            setSelectedReservation(this.emptyReservationObject)
            let position = { x: target.x, y: target.y }
            showHServiceSearch(position, deepCopy(this.timeData))
        },
        openReservationEditPopup() {
            let r = this.getCopiedReservation()
            exitTimeSelectMode()
            setSelectedReservation(r)
            showReservationInfoPopup()
        },
        async copySelectedReservation() {
            let r = this.getCopiedReservation()
            let duration = r.duration
            let endedAt = moment(r.startedAt)
            endedAt.add(duration, 'minute')
            r.endedAt = endedAt
            r.userID = this.reservationSelection.selectedHService.userID
            r.hserviceID = this.reservationSelection.selectedHService._id
            r.hospitalID = this.hospital.selectedHospital._id

            this.sheetState.isSheetLoading = true
            try {
                let result = await createReservation(r, true, this.canSendMessage)
                if (result.data.code != 0) {
                    console.log(result)
                    return
                }

                let newReservation = result.data.data
                exitTimeSelectMode()
                setSelectedReservation(newReservation)
            } finally {
                this.sheetState.isSheetLoading = false
            }
        },
        async cutPasteSelectedReservation() {
            const oldR = deepCopy(this.reservationSelection.selectedReservation)
            let r = this.getCuttingReservation()

            const fromAi = oldR.columnID.isAi && oldR.needConfirm
            const toAi = this.sheetFilters.columns.find(c => c._id === r.columnID).isAi
            const aiMessageType = fromAi && !toAi ? 'confirm' : undefined

            this.sheetState.isSheetLoading = true
            try {
                let result = await updateReservation(r, true, this.canSendMessage, aiMessageType)

                if (result?.data?.code != 0) {
                    console.log(result)
                    return
                }

                let newReservation = result.data.data
                exitTimeSelectMode()
                setSelectedReservation(newReservation)
            } finally {
                this.sheetState.isSheetLoading = false
            }
        },
        getCuttingReservation() {
            let r = this.getCopiedReservation()

            let duration = r.duration
            let endedAt = moment(r.startedAt)
            endedAt.add(duration, 'minute')
            r.endedAt = endedAt
            r = {
                hospitalID: r.hospitalID,
                userID: r.userID,
                hserviceID: r.hserviceID,
                groupID: r.groupID,
                columnID: r.columnID,
                programID: r.programID,
                startedAt: r.startedAt,
                endedAt: r.endedAt
            }
            let group = this.sheetFilters.groups.find((g) => g._id == this.groupID)
            let column = this.sheetFilters.columns.find((c) => c._id == this.columnID)
            r.groupTitle = group.title
            r.columnTitle = column.title
            r._id = this.reservationSelection.selectedReservation._id

            return r
        },
        getCopiedReservation() {
            let r = deepCopy(this.reservationSelection.selectedReservation)
            r.groupID = this.groupID
            r.columnID = this.columnID


            let selectedDate = moment(this.sheetAxis.selectedDate)
            let startedAt = moment(selectedDate)
            let time = moment(this.timeData.time)
            startedAt.set('hours', time.hours()).set('minutes', time.minutes())
            r.startedAt = startedAt
            if (typeToString(r.programID) == 'object') {
                r.programID = r.programID._id
            }
            if (r.programID != undefined) {
                const column = this.sheetFilters.columns.find((c) => c._id == this.columnID)
                if (column) {
                    const newProgram = column.programs.find(p => p._id.toString() === r.programID)
                    if (!newProgram) {
                        const defaultProgram = column.programs.find(p => p.isDefault === true)
                        if (defaultProgram) { r.programID = defaultProgram._id }
                    }
                }
            }
            return r
        },
        selectCurrentPosition(e) {
            const x = this.xPosition
            const y = this.yPosition
            this.selectedAreaHelper.setSelectedRange(x, y, x, y)
            hideCellMenu()
            this.action_RightClick(e)
        }
    }
}

</script>